import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, List, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import MiniCalendar, { dateStr } from "@/components/MiniCalendar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = ["all", "draft", "confirmed", "dispatched", "completed", "cancelled"];
const STATUS_CHIP = { draft: "bg-gray-200 text-gray-700", confirmed: "bg-emerald-100 text-emerald-800", dispatched: "bg-blue-100 text-blue-800", completed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-700" };
const EMPTY = { customer_id: "", contact_name: "", email: "", phone: "", event_date: "", guest_count: 100, venue_id: "", menu_id: "" };

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("list");
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [providers, setProviders] = useState([]);
  const [menus, setMenus] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [selUnits, setSelUnits] = useState([]);
  const [selProviders, setSelProviders] = useState([]);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/bookings", { params: tab === "all" ? {} : { status: tab } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/venues").then((r) => setVenues(r.data.filter((v) => !v.archived))).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data.filter((p) => !p.archived))).catch(() => {});
    api.get("/menus").then((r) => setMenus(r.data.filter((m) => !m.archived))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form.event_date) { setUnitOptions([]); return; }
    api.get(`/bookings/feasibility/check?date=${form.event_date}`).then((r) => setUnitOptions(r.data.units)).catch(() => {});
  }, [form.event_date]);

  const navigateMonth = (delta) => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth());
  };

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const createBooking = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/bookings", {
        customer_id: form.customer_id || null,
        contact_name: form.customer_id ? "" : form.contact_name,
        email: form.customer_id ? null : form.email,
        phone: form.customer_id ? "" : form.phone,
        event_date: form.event_date,
        guest_count: Number(form.guest_count),
        venue_id: form.venue_id || null,
        menu_id: form.menu_id || null,
        unit_ids: selUnits,
        provider_ids: selProviders,
      });
      toast.success(`Booking ${data.booking_ref} created`);
      setCreateOpen(false);
      setForm(EMPTY);
      setSelUnits([]);
      setSelProviders([]);
      navigate(`/bookings/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setCreating(false);
    }
  };

  const byDate = {};
  items.forEach((b) => { (byDate[b.event_date] = byDate[b.event_date] || []).push(b); });

  return (
    <div data-testid="bookings-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Bookings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Confirmed events, dispatch pipeline and financial ledgers.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border bg-white" data-testid="view-toggle">
            <button data-testid="view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${view === "list" ? "bg-ink text-white" : "text-muted-foreground hover:text-ink"}`}><List className="h-3.5 w-3.5" /> List</button>
            <button data-testid="view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${view === "calendar" ? "bg-ink text-white" : "text-muted-foreground hover:text-ink"}`}><CalendarDays className="h-3.5 w-3.5" /> Calendar</button>
          </div>
          <Button data-testid="new-booking-button" onClick={() => setCreateOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
            <Plus className="mr-2 h-4 w-4" /> New Booking
          </Button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border" data-testid="booking-status-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`booking-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${tab === t ? "border-b-2 border-ink text-ink" : "text-muted-foreground hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      {view === "list" ? (
        <div className="border border-border bg-white">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Ref", "Customer", "Event Date", "Venue", "Total", "Status", "Deposit", "Payment"].map((h) => (
                  <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => navigate(`/bookings/${b.id}`)}
                  className="cursor-pointer border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono text-xs font-bold">{b.booking_ref}</td>
                  <td className="px-5 py-3 font-medium">{b.customer_name}</td>
                  <td className="px-5 py-3 tabular-nums">{b.event_date}</td>
                  <td className="px-5 py-3 text-muted-foreground">{b.venue_name || "—"}</td>
                  <td className="px-5 py-3 font-semibold tabular-nums">{fmt(b.quote?.total)}</td>
                  <td className="px-5 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-5 py-3"><StatusBadge status={b.deposit_paid ? "paid" : "unpaid"} /></td>
                  <td className="px-5 py-3" data-testid={`booking-payment-${b.id}`}>
                    {b.fully_paid
                      ? <span className="inline-flex items-center rounded-full border border-emerald-600 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-700">Fully paid</span>
                      : <span className="inline-flex items-center rounded-full border border-amber-500 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-700">{fmt(b.balance)} left</span>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No bookings with this status.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <MiniCalendar year={calYear} month={calMonth} onNavigate={navigateMonth} testid="bookings-calendar"
          dayContent={(ds) => (byDate[ds] || []).map((b) => (
            <button key={b.id} data-testid={`cal-booking-${b.id}`} onClick={() => navigate(`/bookings/${b.id}`)}
              className={`block w-full truncate px-1.5 py-0.5 text-left text-[11px] font-semibold transition-opacity duration-150 hover:opacity-75 ${STATUS_CHIP[b.status] || STATUS_CHIP.draft}`}>
              {b.booking_ref} · {b.customer_name}
            </button>
          ))}
        />
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="new-booking-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Booking (manual)</DialogTitle></DialogHeader>
          <form onSubmit={createBooking} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="col-span-1 sm:col-span-2 space-y-1.5">
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v === "__new__" ? "" : v })}>
                <SelectTrigger data-testid="booking-customer-select"><SelectValue placeholder="New customer…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__new__">— New customer —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!form.customer_id && (
              <>
                <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="booking-name-input" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="booking-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </>
            )}
            <div className="space-y-1.5"><Label>Event date</Label><Input data-testid="booking-date-input" type="date" min={new Date().toISOString().split("T")[0]} required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Guests</Label><Input data-testid="booking-guests-input" type="number" min="1" required value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Venue</Label>
              <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
                <SelectTrigger data-testid="booking-venue-select"><SelectValue placeholder="TBC" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Menu</Label>
              <Select value={form.menu_id} onValueChange={(v) => setForm({ ...form, menu_id: v })}>
                <SelectTrigger data-testid="booking-menu-select"><SelectValue placeholder="Standard pricing" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {menus.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {fmt(m.price_per_head)}/head</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-1.5">
              <Label>Units {form.event_date ? `available on ${form.event_date}` : "(pick a date first)"}</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {unitOptions.map((u) => (
                  <label key={u.id} className={`flex items-center gap-2 border border-border px-3 py-2 text-sm ${u.available ? "cursor-pointer hover:bg-slate-50" : "cursor-not-allowed opacity-50"}`} data-testid={`booking-unit-${u.id}`}>
                    <Checkbox disabled={!u.available} checked={selUnits.includes(u.id)} onCheckedChange={() => toggle(selUnits, setSelUnits, u.id)} />
                    <span className="flex-1 font-medium">{u.name}</span>
                  </label>
                ))}
                {unitOptions.length === 0 && <p className="text-sm text-muted-foreground">Select a date to check availability.</p>}
              </div>
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-1.5">
              <Label>External providers</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {providers.map((p) => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-slate-50" data-testid={`booking-provider-${p.id}`}>
                    <Checkbox checked={selProviders.includes(p.id)} onCheckedChange={() => toggle(selProviders, setSelProviders, p.id)} />
                    <span className="flex-1 font-medium">{p.name}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{fmt(p.rate)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Button data-testid="booking-submit-button" type="submit" disabled={creating} className="w-full rounded-md bg-ink hover:bg-ink-light">
                {creating ? "Creating…" : "Create Booking (draft)"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
